// Gallery and admin API client.

import { ApiError } from '../server/api';

export type ItemStatus = 'pending' | 'approved' | 'rejected' | 'hidden';
export type Kind = 'static' | 'animation';

export interface GalleryItem {
  id: string;
  kind: Kind;
  status?: ItemStatus;
  title: string;
  caption: string;
  mime: string;
  bytes: number;
  width: number | null;
  height: number | null;
  created_at: number;
  file_url: string;
  preview_url: string;
  has_params: boolean;
  params?: Record<string, unknown>;
  ip_hash?: string;
}

export interface Page {
  items: GalleryItem[];
  page: number;
  pages: number;
  total: number;
}

export interface Stats {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
  hidden: number;
}

async function json<T>(r: Response): Promise<T> {
  if (!r.ok) {
    let code = 'http_error';
    let message = `HTTP ${r.status}`;
    try {
      const b = (await r.json()) as { error?: { code: string; message: string } };
      if (b.error) ({ code, message } = b.error);
    } catch {
      /* ignore */
    }
    throw new ApiError(r.status, code, message);
  }
  return r.status === 204 ? (undefined as T) : ((await r.json()) as T);
}

const jsonInit = (method: string, body: unknown): RequestInit => ({
  method,
  headers: { 'content-type': 'application/json' },
  body: JSON.stringify(body),
  credentials: 'same-origin',
});

export const gallery = {
  list: (page = 1, kind: Kind | 'all' = 'all', perPage = 24) =>
    fetch(`/api/gallery?page=${page}&kind=${kind}&per_page=${perPage}`).then((r) => json<Page>(r)),
  item: (id: string) => fetch(`/api/gallery/${id}`).then((r) => json<GalleryItem>(r)),
  submit: (f: FormData) =>
    fetch('/api/gallery', { method: 'POST', body: f }).then((r) => json<{ id: string; status: string }>(r)),
};

export const admin = {
  me: () =>
    fetch('/api/admin/me', { credentials: 'same-origin' }).then((r) =>
      json<{ authenticated: boolean; configured: boolean }>(r),
    ),
  login: (password: string) =>
    fetch('/api/admin/login', jsonInit('POST', { password })).then((r) => json<{ ok: boolean }>(r)),
  logout: () => fetch('/api/admin/logout', { method: 'POST', credentials: 'same-origin' }).then((r) => json<void>(r)),
  queue: (status: ItemStatus | 'all', page = 1, perPage = 30) =>
    fetch(`/api/admin/queue?status=${status}&page=${page}&per_page=${perPage}`, { credentials: 'same-origin' }).then(
      (r) => json<Page>(r),
    ),
  stats: () =>
    fetch('/api/admin/stats', { credentials: 'same-origin' }).then((r) =>
      json<{ stats: Stats; gallery_enabled: boolean; gallery_configured: boolean }>(r),
    ),
  setGallery: (enabled: boolean) =>
    fetch('/api/admin/settings', jsonInit('PUT', { gallery_enabled: enabled })).then((r) =>
      json<{ gallery_enabled: boolean }>(r),
    ),
  action: (id: string, action: 'approve' | 'reject' | 'hide') =>
    fetch(`/api/admin/items/${id}/${action}`, { method: 'POST', credentials: 'same-origin' }).then((r) =>
      json<void>(r),
    ),
  bulk: (ids: string[], action: 'approve' | 'reject' | 'hide' | 'delete') =>
    fetch('/api/admin/items/bulk', jsonInit('POST', { ids, action })).then((r) => json<{ updated: number }>(r)),
  edit: (id: string, title: string, caption?: string) =>
    fetch(`/api/admin/items/${id}`, jsonInit('PUT', { title, caption })).then((r) => json<void>(r)),
  remove: (id: string) =>
    fetch(`/api/admin/items/${id}`, { method: 'DELETE', credentials: 'same-origin' }).then((r) => json<void>(r)),
};

/** First frame of a video blob as a JPEG preview (for galleries without ffmpeg). */
export async function videoPreview(blob: Blob): Promise<Blob | undefined> {
  const url = URL.createObjectURL(blob);
  try {
    const v = document.createElement('video');
    v.muted = true;
    v.src = url;
    await new Promise<void>((resolve, reject) => {
      v.onloadeddata = () => resolve();
      v.onerror = () => reject(new Error('video'));
    });
    v.currentTime = 0;
    await new Promise((r) => setTimeout(r, 50));
    const c = document.createElement('canvas');
    c.width = v.videoWidth;
    c.height = v.videoHeight;
    c.getContext('2d')!.drawImage(v, 0, 0);
    return await new Promise<Blob | undefined>((r) => c.toBlob((b) => r(b ?? undefined), 'image/jpeg', 0.85));
  } catch {
    return undefined;
  } finally {
    URL.revokeObjectURL(url);
  }
}
